import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { clearStoredToken } from "../../api/client";
import type { CRMAccess, CRMUser, Tenant, WorkspaceProduct } from "../../api/types";
import { activeNavigationId, navigationForRole, titleForPath, type NavigationItem } from "../../navigation/navigation";
import { useUnsavedChanges } from "../../navigation/unsavedChangesContext";
import { getSimpleBillingInventoryStatus } from "../../simple-billing/api";
import { simpleBillingQueryKeys } from "../../simple-billing/queryKeys";
import { BrandLogo } from "../BrandLogo/BrandLogo";
import { Button } from "../Button/Button";
import { NavIcon } from "./NavIcon";
import styles from "./AppShell.module.css";

const SIDEBAR_COLLAPSED_STORAGE_KEY = "crm_sidebar_collapsed";

function storedSidebarCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

interface AppShellProps {
  user: CRMUser;
  tenant: Tenant | null;
  features?: string[];
  featureTenantId?: string | null;
  workspaceProduct?: WorkspaceProduct | null;
  crmAccess?: CRMAccess | null;
}

function titleCase(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function AppShell({ user, tenant, features = [], featureTenantId = null, workspaceProduct = null, crmAccess = null }: AppShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const unsavedChanges = useUnsavedChanges();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(storedSidebarCollapsed);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const visibleFeatures = user.role === "super_admin" && !featureTenantId ? [] : features;
  const billingWorkspace = workspaceProduct === "SIMPLE_BILLING" && user.role !== "super_admin";
  const crmActivationRequired = workspaceProduct === "CRM"
    && user.role !== "super_admin"
    && crmAccess?.state === "unactivated";
  const activationNavigation: NavigationItem[] = user.role === "staff"
    ? []
    : [
        { id: "admin", label: "Workspace Setup", path: "/setup", icon: "settings" },
        { id: "subscription", label: "Activate CRM", path: "/subscription", icon: "billing" },
      ];
  const inventoryTenantId = user.role === "super_admin" ? featureTenantId : null;
  const inventoryStatus = useQuery({
    queryKey: simpleBillingQueryKeys.inventoryStatus(inventoryTenantId),
    queryFn: () => getSimpleBillingInventoryStatus(inventoryTenantId),
    enabled: !crmActivationRequired
      && visibleFeatures.includes("SIMPLE_BILLING")
      && (user.role !== "super_admin" || Boolean(featureTenantId)),
    retry: false,
  });
  const navigation = crmActivationRequired
    ? activationNavigation
    : navigationForRole(user.role, visibleFeatures, workspaceProduct, Boolean(inventoryStatus.data?.enabled));
  const billingPath = featureTenantId && user.role === "super_admin"
    ? `/simple-billing?tenant_id=${encodeURIComponent(featureTenantId)}`
    : "/simple-billing";
  const workflowsPath = featureTenantId && user.role === "super_admin"
    ? `/workflows?tenant_id=${encodeURIComponent(featureTenantId)}`
    : "/workflows";
  const projectsPath = featureTenantId && user.role === "super_admin"
    ? `/projects?tenant_id=${encodeURIComponent(featureTenantId)}`
    : "/projects";
  const commercePath = user.role === "staff"
    ? "/commerce/reply-activity"
    : featureTenantId && user.role === "super_admin"
      ? `/commerce/storefront?tenant_id=${encodeURIComponent(featureTenantId)}`
      : "/commerce/storefront";
  const activeId = activeNavigationId(location.pathname);
  const userInitial = user.name.trim().charAt(0).toUpperCase() || "U";
  const product = billingWorkspace ? "billing" : "crm";
  const productName = billingWorkspace ? "LeadCat Billing" : "LeadCat CRM";

  const closeMobileNav = useCallback((restoreFocus = false) => {
    setMobileOpen(false);
    if (restoreFocus) window.requestAnimationFrame(() => toggleRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => closeRef.current?.focus());
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeMobileNav(true);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeMobileNav, mobileOpen]);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(sidebarCollapsed));
    } catch {
      // Keep the sidebar usable when browser storage is unavailable.
    }
  }, [sidebarCollapsed]);

  const signOut = () => {
    if (unsavedChanges.isProtected) {
      if (!window.confirm("Some changes have not finished saving. Leave this page anyway?")) {
        return;
      }
      unsavedChanges.allowNextNavigation();
    }
    clearStoredToken();
    queryClient.clear();
    navigate("/login", { replace: true });
  };

  return (
    <div className={`${styles.shell} ${sidebarCollapsed ? styles.sidebarCollapsed : ""} ${mobileOpen ? styles.mobileOpen : ""}`}>
      <aside className={styles.sidebar} id="react-app-sidebar" data-open={mobileOpen} data-collapsed={sidebarCollapsed}>
        <div className={styles.sidebarHeader}>
          <div className={styles.brand}>
            <BrandLogo className={styles.brandFull} product={product} variant="lockup" size="small" />
            <BrandLogo aria-hidden="true" className={styles.brandCompact} product={product} variant="icon" size="medium" />
            {billingWorkspace ? <span className={styles.brandWorkspace}>{tenant?.name || "Billing workspace"}</span> : null}
          </div>
          <button
            className={styles.desktopNavToggle}
            type="button"
            onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
            aria-label={sidebarCollapsed ? "Expand navigation" : "Collapse navigation"}
            aria-controls="react-app-sidebar"
            aria-expanded={!sidebarCollapsed}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3.5" y="4" width="17" height="16" rx="2.5" />
              <path d="M9 4v16" />
            </svg>
          </button>
          <button
            ref={closeRef}
            className={styles.mobileNavClose}
            type="button"
            onClick={() => closeMobileNav(true)}
            aria-label="Close navigation"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
          </button>
        </div>
        <nav className={styles.nav} aria-label="Primary navigation">
          {navigation.map((item) => (
            <NavLink
              key={item.id}
              className={`${styles.navLink} ${activeId === item.id ? styles.active : ""}`}
              to={item.id === "simple-billing" ? billingPath : item.id === "workflows" ? workflowsPath : item.id === "projects" ? projectsPath : item.id === "commerce" ? commercePath : item.path}
              aria-current={activeId === item.id ? "page" : undefined}
              aria-label={sidebarCollapsed ? item.label : undefined}
              title={sidebarCollapsed ? item.label : undefined}
              onClick={() => closeMobileNav(false)}
            >
              <span className={styles.navIcon}><NavIcon icon={item.icon} /></span>
              <span className={styles.navLabel}>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
      <button
        className={styles.mobileNavBackdrop}
        type="button"
        onClick={() => closeMobileNav(true)}
        tabIndex={-1}
        aria-label="Close navigation"
      />
      <main className={styles.main} inert={mobileOpen ? true : undefined}>
        <header className={styles.topbar}>
          <div className={styles.topbarInner}>
            <div className={styles.topbarLeading}>
              <button
                ref={toggleRef}
                className={styles.mobileNavToggle}
                type="button"
                onClick={() => setMobileOpen(true)}
                aria-label="Open navigation"
                aria-controls="react-app-sidebar"
                aria-expanded={mobileOpen}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
              </button>
              <BrandLogo aria-hidden="true" className={styles.mobileProductMark} product={product} variant="icon" size="small" />
              <div className={styles.pageHeading}>
                <h2>{titleForPath(location.pathname, user.role, visibleFeatures, workspaceProduct)}</h2>
                <div className={styles.pageContext}>
                  <span>{productName}</span>
                  <i aria-hidden="true">·</i>
                  <span>{billingWorkspace ? "Billing workspace" : crmActivationRequired ? "Activation pending" : titleCase(user.role)}</span>
                </div>
              </div>
            </div>
            <div className={styles.topbarMeta}>
              {!billingWorkspace && tenant?.name ? (
                <span className={styles.topbarWorkspace} title={tenant.name}>{tenant.name}</span>
              ) : null}
              <div className={styles.topbarUser}>
                <span className={styles.topbarAvatar} aria-hidden="true">{userInitial}</span>
                <span className={styles.topbarUserName}>{user.name}</span>
              </div>
              <Button className={styles.signOut} onClick={signOut}>{billingWorkspace ? "Logout" : "Sign out"}</Button>
            </div>
          </div>
        </header>
        <section className={styles.content}>
          <Outlet />
        </section>
      </main>
      <div className={styles.toastRegion} aria-live="polite" />
    </div>
  );
}
